/**
 * useOpenHouseCountdown — "Sheet closes in 43m" for the sign-in sheet.
 *
 * WHAT: turns the bootstrap's `sign_in_closes_at` + `server_time` into a
 * live countdown label, re-polls the bootstrap every 60 s and whenever
 * the tab becomes visible again, and reports `isClosed` the moment the
 * countdown reaches zero so the page can flip to its ClosedState.
 *
 * WHY server time: the kiosk is an iPad in Guided Access that may sit
 * on a wrong clock for months, and visitors' phones are no better. We
 * keep a `server − device` offset from every poll and count down
 * against the corrected clock, so the sheet closes when the server
 * says it does, not when the tablet thinks it does.
 *
 * Ticks every 30 s (a minute-granularity label needs no more) plus one
 * precise timeout at the zero instant, so the flip is immediate.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseApiDate } from '@/utils/dateTime';

export interface UseOpenHouseCountdownOptions {
    /** `open_house.sign_in_closes_at` from the bootstrap (naive-UTC ISO). */
    sign_in_closes_at: string | null | undefined;
    /** `server_time` from the same bootstrap response. */
    server_time: string | null | undefined;
    /** Silent re-poll of the bootstrap. Must never flash a loading state. */
    refetch: () => void | Promise<unknown>;
    /** Stop ticking and polling (e.g. once the sheet is known closed). */
    enabled?: boolean;
}

export interface OpenHouseCountdown {
    /** Milliseconds until the sheet closes on the server clock (>= 0). */
    remainingMs: number;
    /** "Sheet closes in 43m" / "Sheet closes in 2h 05m" / "Sheet closed". */
    label: string;
    /** True once the server-corrected clock has passed `sign_in_closes_at`. */
    isClosed: boolean;
    /** Closing within `URGENT_MINUTES` — lets the pill change colour. */
    isUrgent: boolean;
}

const TICK_MS = 30_000;
const POLL_MS = 60_000;
const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const URGENT_MINUTES = 10;
// setTimeout overflows above ~24.8 days; the sheet never stays open that
// long, but guard so a far-future close never fires instantly.
const MAX_TIMEOUT_MS = 2_147_483_647;

function toEpochMs(iso: string | null | undefined): number {
    if (!iso) return Number.NaN;
    return parseApiDate(iso).getTime();
}

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

export function formatCountdownLabel(remainingMs: number, isClosed: boolean): string {
    if (isClosed) return 'Sheet closed';
    const totalMinutes = Math.max(1, Math.ceil(remainingMs / MS_PER_MINUTE));
    if (totalMinutes < MINUTES_PER_HOUR) return `Sheet closes in ${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
    const minutes = totalMinutes % MINUTES_PER_HOUR;
    return `Sheet closes in ${hours}h ${pad2(minutes)}m`;
}

export function useOpenHouseCountdown({
    sign_in_closes_at: closesAtIso,
    server_time: serverTimeIso,
    refetch,
    enabled = true,
}: UseOpenHouseCountdownOptions): OpenHouseCountdown {
    const [deviceNowMs, setDeviceNowMs] = useState(() => Date.now());
    // Latest refetch in a ref so the interval / visibility subscriptions
    // never re-register just because the caller re-created the callback.
    const refetchRef = useRef(refetch);
    useEffect(() => {
        refetchRef.current = refetch;
    }, [refetch]);

    // `server − device` at the moment the bootstrap arrived. Recomputed
    // whenever a poll delivers a fresh `server_time`.
    const offsetMs = useMemo(() => {
        const serverMs = toEpochMs(serverTimeIso);
        return Number.isFinite(serverMs) ? serverMs - Date.now() : 0;
    }, [serverTimeIso]);

    const closesAtMs = useMemo(() => toEpochMs(closesAtIso), [closesAtIso]);
    const correctedNowMs = deviceNowMs + offsetMs;
    const hasCloseTime = Number.isFinite(closesAtMs);
    const remainingMs = hasCloseTime ? Math.max(0, closesAtMs - correctedNowMs) : 0;
    const isClosed = hasCloseTime && remainingMs <= 0;
    const isUrgent = hasCloseTime && !isClosed && remainingMs <= URGENT_MINUTES * MS_PER_MINUTE;

    const poll = useCallback(() => {
        setDeviceNowMs(Date.now());
        void refetchRef.current();
    }, []);

    // 30 s tick + one exact timeout at the zero instant.
    useEffect(() => {
        if (!enabled) return undefined;
        const tick = window.setInterval(() => setDeviceNowMs(Date.now()), TICK_MS);
        let zeroTimer: number | undefined;
        if (hasCloseTime && remainingMs > 0) {
            zeroTimer = window.setTimeout(poll, Math.min(remainingMs, MAX_TIMEOUT_MS));
        }
        return () => {
            window.clearInterval(tick);
            if (zeroTimer !== undefined) window.clearTimeout(zeroTimer);
        };
    }, [enabled, hasCloseTime, remainingMs, poll]);

    // 60 s bootstrap poll + refresh on return to the foreground.
    useEffect(() => {
        if (!enabled) return undefined;
        const interval = window.setInterval(poll, POLL_MS);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') poll();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [enabled, poll]);

    return {
        remainingMs,
        // No close time yet (bootstrap still loading) → nothing to show.
        label: hasCloseTime ? formatCountdownLabel(remainingMs, isClosed) : '',
        isClosed,
        isUrgent,
    };
}

export default useOpenHouseCountdown;
